import { getText } from "shared/lib";
import {
  TreePropertyType,
  updateTreeProperty,
  type SpeciesTreePropertyDescriptor,
  type TreeDescriptor,
} from "../lib";
import { useTreeDialogValidator } from "./lib";

import { Toggle } from "shared/components";
import { FieldLabel } from "./FieldLabel";
import { FieldValidationMessages } from "./FieldValidationMessages";

export interface SpeciesFieldProps {
  onChange: ((treeDescriptor: TreeDescriptor) => void) | undefined;
  treeDescriptor: TreeDescriptor;
}

export function SpeciesField<T extends SpeciesTreePropertyDescriptor>({
  onChange,
  treeDescriptor,
}: SpeciesFieldProps) {
  const { isValid, messages, shouldShowValidation } = useTreeDialogValidator<T>(
    {
      checker: (property) => !!property?.value,
      properties: treeDescriptor.properties,
      treePropertyType: TreePropertyType.species,
    },
  );

  const species = treeDescriptor.properties.get(TreePropertyType.species) as
    | T
    | undefined;

  const handleChange = (value: string) => {
    const newTreeDescriptor = updateTreeProperty<T>(
      treeDescriptor,
      TreePropertyType.species,
      (draft) => {
        draft.value = value as T["value"];
      },
    );

    onChange?.(newTreeDescriptor);
  };

  return (
    <div className="flex flex-col gap-1">
      <FieldLabel isValid={isValid}>{getText("Species")}</FieldLabel>

      <div className="flex gap-2">
        <Toggle
          aria-invalid={!isValid && shouldShowValidation}
          className="aria-invalid:border-redBorder"
          onClick={() => {
            handleChange("native");
          }}
          pressed={species?.value === "native"}
          variant="rounded"
        >
          {getText("Native")}
        </Toggle>
        <Toggle
          aria-invalid={!isValid && shouldShowValidation}
          className="aria-invalid:border-redBorder"
          onClick={() => {
            handleChange("exotic");
          }}
          pressed={species?.value === "exotic"}
          variant="rounded"
        >
          {getText("Exotic")}
        </Toggle>
        <Toggle
          aria-invalid={!isValid && shouldShowValidation}
          className="aria-invalid:border-redBorder"
          onClick={() => {
            handleChange("unsure");
          }}
          pressed={species?.value === "unsure"}
          variant="rounded"
        >
          {getText("Unsure")}
        </Toggle>
      </div>

      <FieldValidationMessages messages={messages} />
    </div>
  );
}
